import { useEffect, useState } from 'react';
import { diagnosisCodeFitnessRouteDiagnosisCodesFitnessPost } from '../services/api/sdk.gen';
import type { PrimaryDiagnosisFitness } from '../services/api/types.gen';
import { isPdgmRelevant } from '../utils/primaryDiagnosisFitness';

interface FitnessScope {
  episodeId?: string;
  visitId?: string;
}

/**
 * Principal-diagnosis fitness for codes this app did not hand the caller.
 *
 * The claim worksheet stores bare code strings and the coding page reads its
 * diagnoses from RogerDoc, so neither can carry fitness on the rows themselves.
 * Both ask the EMR — the same `hipps_icd10_code` table the HIPPS calculator and
 * the claim pipeline read — rather than growing a second copy of the rule.
 *
 * Defaults assume PDGM until the episode says otherwise, so a slow or failed
 * lookup errs toward warning rather than toward silence.
 */
export function usePrimaryDiagnosisFitness(
  codes: string[],
  scope: FitnessScope = {}
) {
  const [fitnessByCode, setFitnessByCode] = useState<
    Record<string, PrimaryDiagnosisFitness>
  >({});
  const [payerContext, setPayerContext] = useState({
    groupsViaPdgm: true,
    payerKnown: false,
  });
  // "We have not asked yet" and "we asked and got no answer" are different
  // facts, and callers gate on the difference: the first is a reason to wait,
  // the second is a reason to warn. Without this flag they are both an absent
  // map entry, so a click landing before the round trip returned drew the
  // could-not-be-checked confirm over a perfectly groupable code.
  const [fitnessPending, setFitnessPending] = useState(false);

  // Codes are a new array each render; key on the content so a re-render with
  // the same codes does not refetch. Sorted, because fitness is per-code and
  // order-independent — keying on the raw order made every drag-reorder refetch
  // an answer that had not changed.
  const codesKey = [...codes].sort().join(',');
  const { episodeId, visitId } = scope;

  useEffect(() => {
    const requested = codesKey ? codesKey.split(',') : [];
    // Still ask when there are no codes but a scope: the caller needs the payer
    // context to decide how loudly to warn about the code it is *about* to
    // seat. Skipping the round trip here left the claim worksheet permanently
    // on the optimistic default, so it full-warned known commercial payers.
    if (requested.length === 0 && !episodeId && !visitId) {
      setFitnessByCode({});
      setFitnessPending(false);
      return;
    }

    let cancelled = false;
    setFitnessPending(true);
    (async () => {
      const response = await diagnosisCodeFitnessRouteDiagnosisCodesFitnessPost(
        {
          body: {
            codes: requested,
            episode_id: episodeId ?? null,
            visit_id: visitId ?? null,
          },
        }
      );
      if (cancelled) return;
      if (response.error || !response.data) {
        // The generated client does not throw; a failed lookup leaves the
        // previous answer in place rather than silently clearing every warning.
        // Pending still clears: a failure is an answer for gating purposes —
        // the one that means warn — and leaving it set would make callers wait
        // for a response that is never coming.
        console.error('Failed to load diagnosis fitness:', response.error);
        setFitnessPending(false);
        return;
      }
      setFitnessPending(false);
      setFitnessByCode(response.data.fitness_by_code);
      setPayerContext({
        // Both default false server-side; absent means "no payer resolved",
        // which isPdgmRelevant reads as warn-at-full-strength.
        groupsViaPdgm: response.data.groups_via_pdgm ?? false,
        payerKnown: response.data.payer_known ?? false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [codesKey, episodeId, visitId]);

  return {
    fitnessByCode,
    fitnessPending,
    ...payerContext,
    pdgmRelevant: isPdgmRelevant(payerContext),
  };
}
